import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaBookOpen, FaImage, FaVideo, FaChalkboardTeacher, FaUpload, FaPlus, FaDoorOpen, FaEye } from 'react-icons/fa';
import {
  addCourse, uploadNote, uploadImage, addVideo,
  getCourses, getNotes, getGallery, getVideos,
  deleteNote, deleteGallery, deleteVideo, deleteCourse,
} from '../api/api';

const TABS = ['Notes', 'Gallery', 'Videos', 'Courses'];

const fileInputStyle = {
  background: 'var(--white)',
  border: '1.5px solid var(--border)',
  borderRadius: 8,
  padding: '0.8rem',
  color: 'var(--dark)',
  width: '100%',
  fontFamily: 'inherit',
};

const panelStyle = {
  background: 'var(--white)',
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: '1.8rem',
  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

// ── Delete Confirmation Modal ─────────────────────────────────
function ConfirmModal({ item, onConfirm, onCancel }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: 'var(--white)', borderRadius: 16,
        padding: '2rem', maxWidth: 420, width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'fadeInUp 0.2s ease',
      }}>
        {/* Icon */}
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: '#FEF2F2', border: '2px solid #FECACA',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.2rem', fontSize: '1.6rem', color: 'var(--red)',
        }}><FaTrash size={24} /></div>

        <h3 style={{ textAlign: 'center', fontWeight: 800, color: 'var(--dark)', marginBottom: '0.5rem' }}>
          Delete Confirmation
        </h3>
        <p style={{ textAlign: 'center', color: 'var(--gray)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
          Are you sure you want to delete this item?
        </p>
        {item && (
          <p style={{
            textAlign: 'center', fontWeight: 600, color: 'var(--dark)',
            background: 'var(--bg)', borderRadius: 8, padding: '0.5rem 1rem',
            fontSize: '0.88rem', marginBottom: '1.5rem',
          }}>
            "{item}"
          </p>
        )}
        <p style={{ textAlign: 'center', color: 'var(--red)', fontSize: '0.82rem', marginBottom: '1.5rem' }}>
          ⚠️ This action cannot be undone.
        </p>

        <div style={{ display: 'flex', gap: '0.8rem' }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '0.8rem', borderRadius: 8,
              border: '1.5px solid var(--border)', background: 'var(--white)',
              color: 'var(--dark)', fontWeight: 600, cursor: 'pointer',
              fontSize: '0.9rem', transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--white)'}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1, padding: '0.8rem', borderRadius: 8,
              border: 'none', background: 'var(--red)',
              color: '#fff', fontWeight: 700, cursor: 'pointer',
              fontSize: '0.9rem', transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--red-dark)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--red)'}
          >
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Button ─────────────────────────────────────────────
function DelBtn({ onClick }) {
  return (
    <button
      onClick={onClick}
      title="Delete"
      style={{
        background: '#FEF2F2', border: '1px solid #FECACA',
        color: 'var(--red)', borderRadius: 6,
        padding: '0.35rem 0.75rem', cursor: 'pointer',
        fontSize: '0.8rem', fontWeight: 600,
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        transition: 'background 0.2s, color 0.2s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = '#fff'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = 'var(--red)'; }}
    >
      <FaTrash size={12} /> Delete
    </button>
  );
}

// ── Main Admin Component ──────────────────────────────────────
export default function Admin() {
  const [tab, setTab] = useState('Notes');
  const [msg, setMsg] = useState(null);
  const [lists, setLists] = useState({ notes: [], gallery: [], videos: [], courses: [] });
  const [confirm, setConfirm] = useState(null); // { id, label, type }
  const navigate = useNavigate();

  const notify = (ok, text) => {
    setMsg({ ok, text });
    setTimeout(() => setMsg(null), 3500);
  };

  const fetchAll = useCallback(async () => {
    const [n, g, v, c] = await Promise.allSettled([getNotes(), getGallery(), getVideos(), getCourses()]);
    setLists({
      notes:   n.status === 'fulfilled' ? n.value.data : [],
      gallery: g.status === 'fulfilled' ? g.value.data : [],
      videos:  v.status === 'fulfilled' ? v.value.data : [],
      courses: c.status === 'fulfilled' ? c.value.data : [],
    });
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const logout = () => {
    localStorage.removeItem('sbs_token');
    navigate('/admin');
  };

  // ── Delete handler ────────────────────────────────────────────
  const askDelete = (id, label, type) => setConfirm({ id, label, type });

  const handleDelete = async () => {
    if (!confirm) return;
    const { id, type } = confirm;
    setConfirm(null);
    try {
      if (type === 'note')    await deleteNote(id);
      if (type === 'gallery') await deleteGallery(id);
      if (type === 'video')   await deleteVideo(id);
      if (type === 'course')  await deleteCourse(id);
      notify(true, '✅ Item deleted successfully!');
      fetchAll();
    } catch {
      notify(false, '❌ Delete failed. Please try again.');
    }
  };

  // ── Upload Forms ──────────────────────────────────────────────
  const NoteForm = () => {
    const [title, setTitle] = useState('');
    const [className, setClassName] = useState('Class 11');
    const [subject, setSubject] = useState('Physics');
    const [chapterNumber, setChapterNumber] = useState('');
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const selectStyle = {
      background: 'var(--white)', border: '1.5px solid var(--border)',
      borderRadius: 8, padding: '0.8rem 1rem', color: 'var(--dark)',
      fontSize: '0.95rem', fontFamily: 'inherit', width: '100%',
    };

    const submit = async e => {
      e.preventDefault(); setBusy(true);
      const fd = new FormData();
      fd.append('title', title); fd.append('file', file);
      fd.append('className', className); fd.append('subject', subject);
      if (chapterNumber) fd.append('chapterNumber', chapterNumber);
      try {
        await uploadNote(fd);
        notify(true, '✅ Note uploaded successfully!');
        setTitle(''); setChapterNumber(''); setFile(null);
        e.target.reset(); fetchAll();
      } catch { notify(false, '❌ Upload failed.'); }
      finally { setBusy(false); }
    };

    return (
      <form onSubmit={submit} className="contact-form">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
          <div className="form-group">
            <label>Class</label>
            <select value={className} onChange={e => setClassName(e.target.value)} style={selectStyle}>
              {['Class 9', 'Class 10', 'Class 11', 'Class 12'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Subject</label>
            <select value={subject} onChange={e => setSubject(e.target.value)} style={selectStyle}>
              {['Physics', 'Chemistry', 'Maths', 'Biology'].map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Chapter Number</label>
          <input type="number" min="1" value={chapterNumber} onChange={e => setChapterNumber(e.target.value)} placeholder="e.g. 1" />
        </div>
        <div className="form-group">
          <label>Chapter Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Units & Measurements" required />
        </div>
        <div className="form-group">
          <label>PDF File</label>
          <input type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} required style={fileInputStyle} />
        </div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ opacity: busy ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <FaUpload size={14} /> {busy ? 'Uploading...' : 'Upload Note'}
        </button>
      </form>
    );
  };

  const GalleryForm = () => {
    const [caption, setCaption] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const onFile = e => {
      const f = e.target.files[0]; setFile(f);
      if (f) setPreview(URL.createObjectURL(f));
    };

    const submit = async e => {
      e.preventDefault(); setBusy(true);
      const fd = new FormData();
      fd.append('image', file);
      if (caption) fd.append('caption', caption);
      try {
        await uploadImage(fd);
        notify(true, '✅ Image uploaded successfully!');
        setCaption(''); setFile(null); setPreview(null);
        e.target.reset(); fetchAll();
      } catch { notify(false, '❌ Upload failed.'); }
      finally { setBusy(false); }
    };

    return (
      <form onSubmit={submit} className="contact-form">
        {preview && (
          <img src={preview} alt="preview" style={{
            width: '100%', maxHeight: 180, objectFit: 'cover',
            borderRadius: 8, border: '2px solid var(--blue-light)', marginBottom: '0.8rem',
          }} />
        )}
        <div className="form-group">
          <label>Image File</label>
          <input type="file" accept="image/*" onChange={onFile} required style={fileInputStyle} />
        </div>
        <div className="form-group">
          <label>Caption (optional)</label>
          <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="e.g. Annual Day 2024" />
        </div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ opacity: busy ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <FaUpload size={14} /> {busy ? 'Uploading...' : 'Upload Image'}
        </button>
      </form>
    );
  };

  const VideoForm = () => {
    const [f, setF] = useState({ title: '', videoUrl: '' });
    const [busy, setBusy] = useState(false);

    const submit = async e => {
      e.preventDefault(); setBusy(true);
      try {
        await addVideo(f);
        notify(true, '✅ Video added successfully!');
        setF({ title: '', videoUrl: '' }); fetchAll();
      } catch { notify(false, '❌ Failed to add video.'); }
      finally { setBusy(false); }
    };

    return (
      <form onSubmit={submit} className="contact-form">
        <div className="form-group">
          <label>Video Title</label>
          <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="e.g. Physics Demo – Newton's Laws" required />
        </div>
        <div className="form-group">
          <label>YouTube URL</label>
          <input value={f.videoUrl} onChange={e => setF({ ...f, videoUrl: e.target.value })} placeholder="https://youtube.com/watch?v=..." required />
        </div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ opacity: busy ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <FaPlus size={14} /> {busy ? 'Adding...' : 'Add Video'}
        </button>
      </form>
    );
  };

  const CourseForm = () => {
    const [f, setF] = useState({ title: '', description: '' });
    const [busy, setBusy] = useState(false);

    const submit = async e => {
      e.preventDefault(); setBusy(true);
      try {
        await addCourse(f);
        notify(true, '✅ Course added successfully!');
        setF({ title: '', description: '' }); fetchAll();
      } catch { notify(false, '❌ Failed to add course.'); }
      finally { setBusy(false); }
    };

    return (
      <form onSubmit={submit} className="contact-form">
        <div className="form-group">
          <label>Course Title</label>
          <input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="e.g. IIT-JEE Advanced Batch" required />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={f.description} onChange={e => setF({ ...f, description: e.target.value })} placeholder="Course details..." required />
        </div>
        <button type="submit" className="btn-primary" disabled={busy} style={{ opacity: busy ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
          <FaPlus size={14} /> {busy ? 'Adding...' : 'Add Course'}
        </button>
      </form>
    );
  };

  // ── Item Lists with Delete buttons ───────────────────────────
  const notesList = (
    lists.notes.length === 0
      ? <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginTop: '1rem' }}>No notes uploaded yet.</p>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
          {lists.notes.map(n => (
            <div key={n.id} className="note-card">
              <div className="note-info">
                <span className="note-icon"><FaBookOpen size={18} color="var(--blue)" /></span>
                <div>
                  <div className="note-title">{n.title}</div>
                  <div className="note-subtitle">
                    {[n.className, n.subject, n.chapterNumber ? `Ch. ${n.chapterNumber}` : null].filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 }}>
                <a href={`https://sbs-coaching-backend-production.up.railway.app${n.fileUrl}`} className="btn-outline-blue"
                  style={{ padding: '0.35rem 0.8rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }} target="_blank" rel="noreferrer">
                  <FaEye size={12} /> View
                </a>
                <DelBtn onClick={() => askDelete(n.id, n.title, 'note')} />
              </div>
            </div>
          ))}
        </div>
  );

  const galleryList = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
      {lists.gallery.length === 0
        ? <p style={{ color: 'var(--gray)', fontSize: '0.9rem' }}>No images uploaded yet.</p>
        : lists.gallery.map(img => (
            <div key={img.id} style={{
              display: 'flex', alignItems: 'center', gap: '0.8rem',
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '0.6rem 0.8rem',
            }}>
              <img
                src={img.imageUrl?.startsWith('http') ? img.imageUrl : `https://sbs-coaching-backend-production.up.railway.app${img.imageUrl}`}
                alt={img.caption || ''}
                style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--dark)', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {img.caption || 'No caption'}
                </div>
                <div style={{ color: 'var(--gray)', fontSize: '0.75rem', marginTop: '0.1rem' }}>Image #{img.id}</div>
              </div>
              <DelBtn onClick={() => askDelete(img.id, img.caption || `Image #${img.id}`, 'gallery')} />
            </div>
          ))
      }
    </div>
  );

  const videosList = (
    lists.videos.length === 0
      ? <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginTop: '1rem' }}>No videos added yet.</p>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
          {lists.videos.map(v => (
            <div key={v.id} style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '0.8rem 1rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.8rem',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: 'var(--dark)', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <FaVideo size={14} color="#B45309" /> {v.title}
                </div>
                <a href={v.videoUrl} target="_blank" rel="noreferrer"
                  style={{ color: 'var(--blue-mid)', fontSize: '0.78rem', fontWeight: 500 }}>
                  {v.videoUrl?.length > 40 ? v.videoUrl.slice(0, 40) + '...' : v.videoUrl}
                </a>
              </div>
              <DelBtn onClick={() => askDelete(v.id, v.title, 'video')} />
            </div>
          ))}
        </div>
  );

  const coursesList = (
    lists.courses.length === 0
      ? <p style={{ color: 'var(--gray)', fontSize: '0.9rem', marginTop: '1rem' }}>No courses added yet.</p>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '1rem' }}>
          {lists.courses.map(c => (
            <div key={c.id} style={{
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '0.8rem 1rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.8rem',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: 'var(--blue)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}><FaChalkboardTeacher size={14} /> {c.title}</div>
                {c.description && <div style={{ color: 'var(--gray)', fontSize: '0.83rem', marginTop: '0.2rem' }}>{c.description}</div>}
              </div>
              <DelBtn onClick={() => askDelete(c.id, c.title, 'course')} />
            </div>
          ))}
        </div>
  );

  const listConfig = {
    Notes:   { form: <NoteForm />,   list: notesList },
    Gallery: { form: <GalleryForm />, list: galleryList },
    Videos:  { form: <VideoForm />,  list: videosList },
    Courses: { form: <CourseForm />, list: coursesList },
  };

  const stats = [
    { label: 'Notes',   count: lists.notes.length,   icon: <FaBookOpen size={28} />, color: '#EFF6FF', border: '#BFDBFE', text: 'var(--blue)', iconColor: '#2563EB' },
    { label: 'Images',  count: lists.gallery.length,  icon: <FaImage size={28} />,  color: '#F0FDF4', border: '#BBF7D0', text: 'var(--green-dark)', iconColor: '#16A34A' },
    { label: 'Videos',  count: lists.videos.length,   icon: <FaVideo size={28} />, color: '#FFFBEB', border: '#FDE68A', text: '#B45309', iconColor: '#F59E0B' },
    { label: 'Courses', count: lists.courses.length,  icon: <FaChalkboardTeacher size={28} />, color: '#FEF2F2', border: '#FECACA', text: 'var(--red-dark)', iconColor: '#EF4444' },
  ];

  const tabKey = tab.toLowerCase();
  const listCount = lists[tabKey === 'gallery' ? 'gallery' : tabKey === 'notes' ? 'notes' : tabKey === 'videos' ? 'videos' : 'courses']?.length || 0;

  return (
    <div className="page">
      {/* Confirm Modal */}
      {confirm && (
        <ConfirmModal
          item={confirm.label}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--dark)' }}>
            Admin <span style={{ color: 'var(--blue-mid)' }}>Panel</span>
          </h2>
          <p style={{ color: 'var(--gray)', marginTop: '0.2rem', fontSize: '0.9rem' }}>Manage institute content</p>
          <div className="accent-line" style={{ margin: '0.6rem 0 0' }} />
        </div>
        <button onClick={logout} className="btn-outline-blue" style={{ padding: '0.6rem 1.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FaDoorOpen size={16} /> Logout
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        {stats.map(({ label, count, icon, color, border, text, iconColor }) => (
          <div key={label} style={{
            background: color, border: `1px solid ${border}`,
            borderRadius: 12, padding: '1.2rem', textAlign: 'center',
            transition: 'all 0.3s ease', cursor: 'default',
          }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 8px 20px ${border}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{ fontSize: '1.8rem', color: iconColor, display: 'flex', justifyContent: 'center', transition: 'all 0.3s ease' }}>{icon}</div>
            <div style={{ fontSize: '1.9rem', fontWeight: 800, color: text, lineHeight: 1.2 }}>{count}</div>
            <div style={{ color: 'var(--gray)', fontSize: '0.82rem', marginTop: '0.2rem' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '0.55rem 1.3rem', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
            border: `2px solid ${tab === t ? 'var(--blue-mid)' : 'var(--border)'}`,
            background: tab === t ? 'var(--blue)' : 'var(--white)',
            color: tab === t ? 'var(--white)' : 'var(--gray)',
            transition: 'all 0.2s', fontSize: '0.88rem',
          }}>{t}</button>
        ))}
      </div>

      {/* Alert */}
      {msg && (
        <div className={`alert ${msg.ok ? 'alert-success' : 'alert-error'}`}
          style={{ marginBottom: '1.5rem', maxWidth: 520 }}>
          {msg.text}
        </div>
      )}

      {/* Upload + List panels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', alignItems: 'start' }}>
        <div style={panelStyle}>
          <h3 style={{ color: 'var(--blue)', fontWeight: 700, marginBottom: '1.5rem', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FaUpload size={16} /> Upload {tab}
          </h3>
          {listConfig[tab].form}
        </div>
        <div style={panelStyle}>
          <h3 style={{ color: 'var(--blue)', fontWeight: 700, fontSize: '1rem', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {tab === 'Notes' ? <FaBookOpen size={16} /> : tab === 'Gallery' ? <FaImage size={16} /> : tab === 'Videos' ? <FaVideo size={16} /> : <FaChalkboardTeacher size={16} />} {tab} List ({listCount})
          </h3>
          {listConfig[tab].list}
        </div>
      </div>
    </div>
  );
}
